/**
 * @dnd-kit modifiers for the planner grid.
 * These functions transform drag coordinates to snap to grid boundaries.
 *
 * @see https://docs.dndkit.com/api-documentation/modifiers
 */

import { CELL_WIDTH, ROW_HEIGHT } from './gridConstants';

/**
 * Creates a snap-to-grid modifier for @dnd-kit.
 * Snaps the drag transform to align with grid cell boundaries.
 *
 * @param {number} cellWidth - Width of each grid cell (defaults to CELL_WIDTH)
 * @param {number} rowHeight - Height of each grid row (defaults to ROW_HEIGHT)
 * @returns {Function} Modifier function for @dnd-kit DndContext
 *
 * @example
 * import { DndContext } from '@dnd-kit/core';
 * import { createSnapToGridModifier } from './dndModifiers';
 *
 * const snapModifier = createSnapToGridModifier();
 *
 * <DndContext modifiers={[snapModifier]}>
 *   ...
 * </DndContext>
 */
export const createSnapToGridModifier = (
  cellWidth = CELL_WIDTH,
  rowHeight = ROW_HEIGHT,
) => ({ transform }) => {
  if (!transform) {
    return transform;
  }

  // Snap X to cell width boundaries
  const snappedX = Math.round(transform.x / cellWidth) * cellWidth;

  // Snap Y to row height boundaries
  const snappedY = Math.round(transform.y / rowHeight) * rowHeight;

  return {
    ...transform,
    x: snappedX,
    y: snappedY,
    scaleX: 1,
    scaleY: 1,
  };
};

/**
 * Modifier that restricts drag movement to horizontal only.
 * Useful for resizing blocks where vertical movement should be prevented.
 *
 * @param {Object} args - Modifier arguments from @dnd-kit
 * @returns {Object} Modified transform with y locked to 0
 */
export const restrictToHorizontalAxis = ({ transform }) => {
  if (!transform) {
    return transform;
  }

  return {
    ...transform,
    y: 0,
  };
};

/**
 * Modifier that restricts drag movement to vertical only.
 * Useful for row reordering where horizontal movement should be prevented.
 *
 * @param {Object} args - Modifier arguments from @dnd-kit
 * @returns {Object} Modified transform with x locked to 0
 */
export const restrictToVerticalAxis = ({ transform }) => {
  if (!transform) {
    return transform;
  }

  return {
    ...transform,
    x: 0,
  };
};

/**
 * Creates a modifier that restricts drag to grid boundaries.
 * Prevents blocks from being dragged outside the grid area.
 *
 * @param {Object} options - Configuration options
 * @param {number} options.minCol - Minimum column index (default: 0)
 * @param {number} options.maxCol - Maximum column index
 * @param {number} options.minRow - Minimum row number (default: 1)
 * @param {number} options.maxRow - Maximum row number
 * @param {number} options.cellWidth - Width of each cell (default: CELL_WIDTH)
 * @param {number} options.rowHeight - Height of each row (default: ROW_HEIGHT)
 * @returns {Function} Modifier function for @dnd-kit DndContext
 */
export const createRestrictToGridModifier = ({
  minCol = 0,
  maxCol,
  minRow = 1,
  maxRow,
  cellWidth = CELL_WIDTH,
  rowHeight = ROW_HEIGHT,
}) => ({ transform, draggingNodeRect }) => {
  if (!transform || !draggingNodeRect) {
    return transform;
  }

  let { x, y } = transform;

  // Calculate current block position in grid coordinates
  const currentCol = Math.round(draggingNodeRect.left / cellWidth);
  const currentRow = Math.round(draggingNodeRect.top / rowHeight) + 1;

  // Calculate target position
  const targetCol = currentCol + Math.round(x / cellWidth);
  const targetRow = currentRow + Math.round(y / rowHeight);

  // Clamp to boundaries (always apply min, optionally apply max)
  let clampedTargetCol = Math.max(targetCol, minCol);
  if (maxCol !== undefined) {
    clampedTargetCol = Math.min(clampedTargetCol, maxCol);
  }
  x = (clampedTargetCol - currentCol) * cellWidth;

  let clampedTargetRow = Math.max(targetRow, minRow);
  if (maxRow !== undefined) {
    clampedTargetRow = Math.min(clampedTargetRow, maxRow);
  }
  y = (clampedTargetRow - currentRow) * rowHeight;

  return {
    ...transform,
    x,
    y,
  };
};

/**
 * Combines multiple modifiers into a single modifier function.
 * Applies modifiers in order, passing the result of each to the next.
 *
 * @param {...Function} modifiers - Modifier functions to combine
 * @returns {Function} Combined modifier function
 *
 * @example
 * const combinedModifier = composeModifiers(
 *   createSnapToGridModifier(),
 *   createRestrictToGridModifier({ maxCol: 52, maxRow: 8 })
 * );
 */
export const composeModifiers = (...modifiers) => (args) => {
  let result = args;

  modifiers.forEach((modifier) => {
    if (typeof modifier === 'function') {
      const modifiedTransform = modifier(result);
      result = {
        ...result,
        transform: modifiedTransform,
      };
    }
  });

  return result.transform;
};

/**
 * Creates a modifier that adds visual feedback by adjusting scale during drag.
 * Can be used to make blocks appear "lifted" when dragged.
 *
 * @param {number} scale - Scale factor during drag (default: 1.02)
 * @returns {Function} Modifier function
 */
export const createDragScaleModifier = (scale = 1.02) => ({ transform }) => {
  if (!transform) {
    return transform;
  }

  return {
    ...transform,
    scaleX: scale,
    scaleY: scale,
  };
};

/**
 * Default snap modifier using planner grid constants.
 * Pre-configured for convenience.
 */
export const snapToGridModifier = createSnapToGridModifier();
